import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../Auth/AuthContext";
import { GeneralAppContext } from "../General/GeneralAppProvider";
import { ACCOUNT_TYPES } from "../utils";
import { AppPreferencesContext } from "../General/AppPreferencesProvider";
import type { AuthContextType } from "../types/AuthProvider";
import type { AppPreferencesContextType } from "../types/AppPreferencesProvider";
import type { GeneralAppContextType } from "../types/GeneralAppProvider";
import { LDProvider } from "launchdarkly-react-client-sdk";
import { PostHogProvider } from "@posthog/react";
import posthog from "posthog-js";
import {
  getEnv,
  initAllNonAuthenticatedServices,
  initAndIdentifyAllServices,
  isProduction,
  teardownAllServices,
} from "./ThirdPartyProviders";
import useEventTracker from "../UserAnalytics/useEventTracker";
import { setAnalyticsEnabled } from "@SeemplicityDev/shared";
import SHA256 from "crypto-js/sha256";

type InitializationStatus = "initialized" | "not-initialized" | "initializing";

// ===== Initialize all services on module load ============
initAllNonAuthenticatedServices();

interface ThirdPartyProviderProps {
  children: React.ReactNode;
}

const ThirdPartyProvider: React.FC<ThirdPartyProviderProps> = ({
  children,
}) => {
  const {
    authState: {
      user,
      username,
      hashedUserId,
      organization,
      account,
      authenticated,
      context,
    },
    isImpersonating,
  } = useContext(AuthContext) as AuthContextType;
  const {
    appPreferences: { role, accountType },
  } = useContext(AppPreferencesContext) as AppPreferencesContextType;

  const {
    appContext: { currentTheme },
  } = useContext(GeneralAppContext) as unknown as GeneralAppContextType;

  const initializationStatusRef =
    useRef<InitializationStatus>("not-initialized");

  const [startTracking, setStartTracking] = useState(false);

  const isRealUser = useMemo(() => {
    return (
      !!authenticated &&
      !isImpersonating &&
      (accountType === ACCOUNT_TYPES.CUSTOMER ||
        accountType === ACCOUNT_TYPES.PROSPECT)
    );
  }, [authenticated, isImpersonating, accountType]);

  // Only production sessions belonging to real users may send analytics.
  const canSendAnalytics = startTracking && isRealUser && isProduction;

  // Start tracking events when the third party services are initialized and the user is a real user
  useEventTracker(canSendAnalytics);

  // Apply the same rule to every imperative trackEvent() call site, which reads
  // this gate at call time. Without it those calls would fire for impersonated
  // sessions and outside production, which the declarative tracker above never
  // does. Flips back to false on logout, when startTracking resets.
  useEffect(() => {
    setAnalyticsEnabled(canSendAnalytics);
  }, [canSendAnalytics]);

  useEffect(() => {
    const hasAllRequiredData =
      authenticated &&
      !!user?.username &&
      !!username &&
      !!hashedUserId &&
      !!organization?.slug &&
      !!account?.name &&
      !!role &&
      !!currentTheme;

    if (
      hasAllRequiredData &&
      initializationStatusRef.current === "not-initialized"
    ) {
      initializationStatusRef.current = "initializing";
      initAndIdentifyAllServices({
        isRealUser,
        email: username,
        userId: user.username,
        hashedUserId,
        organization: organization.slug,
        account: account.name,
        role,
        accountType,
        theme: currentTheme,
      });
      initializationStatusRef.current = "initialized";
      setStartTracking(true);
    }
  }, [
    isRealUser,
    user,
    username,
    hashedUserId,
    organization,
    account,
    role,
    accountType,
    authenticated,
    currentTheme,
  ]);

  useEffect(() => {
    if (!authenticated && initializationStatusRef.current === "initialized") {
      initializationStatusRef.current = "not-initialized";
      teardownAllServices();
      setStartTracking(false);
    }
  }, [authenticated]);

  return (
    <LDProvider
      clientSideID={getEnv("VITE_LAUNCH_DARKLY_CLIENT_ID")}
      context={{
        kind: "multi",
        organization: {
          key: SHA256(organization?.slug ?? "").toString(),
        },
        user: {
          key: SHA256(context?.mainDomain ?? "").toString(),
        },
      }}
    >
      {/* PostHog — `init`/`identify` run only in production and are
          deferred to auth in ThirdPartyProviders; the shared singleton means
          capture calls are safe no-ops when PostHog is uninitialized (i.e.
          outside prod). Provider added for app-side React context. */}
      <PostHogProvider client={posthog}>{children}</PostHogProvider>
    </LDProvider>
  );
};

export default ThirdPartyProvider;
